// src/pages/reports/DailyOrdersSummary.tsx

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Search, X, ChevronDown, Calendar, Download, Filter } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { ordersAPI, branchesAPI, productsAPI, departmentAPI } from '../services/api';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  startOfDay,
  endOfDay,
  subDays,
  subMonths,
  format,
  parseISO,
  isSameDay,
  addHours,
} from 'date-fns';
import { arSA } from 'date-fns/locale';

// ==============================
// === INTERFACES ===
// ==============================
interface OrderRow {
  id: string;
  product: string;
  unit: string;
  department: string;
  departmentId: string;
  branchQuantities: { [branch: string]: number };
  totalQuantity: number;
}

interface Branch {
  _id: string;
  name: string;
  nameEn?: string;
  displayName: string;
}

interface Product {
  _id: string;
  name: string;
  nameEn?: string;
  unit?: string;
  unitEn?: string;
  department: { _id: string; name: string; nameEn?: string };
}

interface Department {
  _id: string;
  name: string;
  nameEn?: string;
}

// ==============================
// === CONSTANTS ===
// ==============================
const PERIOD_OPTIONS = [
  { value: 'today', labelAr: 'اليوم', labelEn: 'Today' },
  { value: 'yesterday', labelAr: 'أمس', labelEn: 'Yesterday' },
  { value: 'week', labelAr: 'آخر 7 أيام', labelEn: 'Last 7 Days' },
  { value: 'month', labelAr: 'آخر 30 يوم', labelEn: 'Last 30 Days' },
  { value: 'custom', labelAr: 'فترة مخصصة', labelEn: 'Custom Range' },
];

// ==============================
// === UTILS ===
// ==============================
const safeString = (value: any, fallback = '') => (typeof value === 'string' ? value.trim() : fallback);
const safeNumber = (value: any, fallback = 0) => (typeof value === 'number' && !isNaN(value) ? value : fallback);
const isValidDate = (date: string) => !isNaN(new Date(date).getTime());

const formatNumberWithComma = (num: number): string => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const toArabicNumerals = (number: string | number): string => {
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return String(number).replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
};

const formatNumber = (num: number, isRtl: boolean): string => {
  const formatted = formatNumberWithComma(num);
  return isRtl ? toArabicNumerals(formatted) : formatted;
};

// ==============================
// === SUB-COMPONENTS ===
// ==============================
export const ProductSearchInput = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  ariaLabel: string;
}) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full ${isRtl ? 'pl-10 pr-3' : 'pr-10 pl-3'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white text-[12px] ${isRtl ? 'text-right' : 'text-left'}`}
        aria-label={ariaLabel}
      />
      {value ? (
        <button
          onClick={() => onChange({ target: { value: '' } } as any)}
          className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-amber-600`}
          aria-label={isRtl ? 'مسح' : 'Clear'}
        >
          <X className="w-4 h-4" />
        </button>
      ) : (
        <Search className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4`} />
      )}
    </div>
  );
};

const Button: React.FC<{
  variant: 'primary' | 'outline';
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  icon?: React.ReactNode;
}> = ({ variant, onClick, disabled, children, icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-all ${
      variant === 'primary'
        ? 'bg-amber-600 hover:bg-amber-700 text-white shadow'
        : 'border border-gray-300 hover:bg-gray-50 text-gray-700'
    } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
  >
    {icon && icon}
    {children}
  </button>
);

// Modified to put "All Departments" at the END of the list
const MultiSelectDropdown: React.FC<{
  value: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string }[];
  ariaLabel: string;
}> = ({ value, onChange, options, ariaLabel }) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleOption = (optionValue: string) => {
    if (optionValue === 'all') {
      onChange(value.includes('all') ? [] : ['all']);
    } else {
      if (value.includes('all')) {
        onChange([optionValue]);
      } else {
        if (value.includes(optionValue)) {
          const newValues = value.filter(v => v !== optionValue);
          onChange(newValues.length === 0 ? ['all'] : newValues);
        } else {
          onChange([...value.filter(v => v !== 'all'), optionValue]);
        }
      }
    }
  };

  const allSelected = value.includes('all') || value.length === options.length - 1;
  const count = value.filter(v => v !== 'all').length;
  const displayLabel = allSelected
    ? isRtl ? 'جميع الأقسام' : 'All Departments'
    : isRtl
      ? `${count} ${count === 1 ? 'قسم' : 'أقسام'}`
      : `${count} ${count === 1 ? 'Department' : 'Departments'}`;

  // Move 'all' option to the end
  const displayOptions = [...options.filter(opt => opt.value !== 'all'), options.find(opt => opt.value === 'all')!];

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        dir={isRtl ? 'rtl' : 'ltr'}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white shadow-sm text-xs font-medium text-gray-700 flex justify-between items-center ${isRtl ? 'text-right' : 'text-left'}`}
        aria-label={ariaLabel}
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto font-medium ${isRtl ? 'text-right' : 'text-left'}`}
        >
          {displayOptions.map(opt => (
            <div key={opt.value} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
              <span className="text-xs text-gray-700">{opt.label}</span>
              <input
                type="checkbox"
                checked={value.includes(opt.value) || (opt.value === 'all' && allSelected)}
                onChange={() => toggleOption(opt.value)}
                className="h-4 w-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
              />
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
};

const PeriodDropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  ariaLabel: string;
}> = ({ value, onChange, options, ariaLabel }) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        dir={isRtl ? 'rtl' : 'ltr'}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white shadow-sm text-xs font-medium text-gray-700 flex justify-between items-center ${isRtl ? 'text-right' : 'text-left'}`}
        aria-label={ariaLabel}
      >
        <span className="truncate">{selectedOption?.label || ''}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto font-medium ${isRtl ? 'text-right' : 'text-left'}`}
        >
          {options.map(opt => (
            <button
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-xs ${isRtl ? 'text-right' : 'text-left'} text-gray-700 hover:bg-amber-50 hover:text-amber-700`}
            >
              {opt.label}
            </button>
          ))}
        </motion.div>
      )}
    </div>
  );
};

// ==============================
// === PDF EXPORT ===
// ==============================
const loadAmiriFont = async (doc: jsPDF): Promise<boolean> => {
  const fontName = 'Amiri';
  try {
    const [regularRes, boldRes] = await Promise.all([
      fetch('https://cdn.jsdelivr.net/gh/aliftype/amiri@master/fonts/Amiri-Regular.ttf'),
      fetch('https://cdn.jsdelivr.net/gh/aliftype/amiri@master/fonts/Amiri-Bold.ttf'),
    ]);
    const [regularFontBytes, boldFontBytes] = await Promise.all([
      regularRes.arrayBuffer(),
      boldRes.arrayBuffer(),
    ]);

    const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
      let binary = '';
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      return window.btoa(binary);
    };

    doc.addFileToVFS(`${fontName}-normal.ttf`, arrayBufferToBase64(regularFontBytes));
    doc.addFont(`${fontName}-normal.ttf`, fontName, 'normal');
    doc.addFileToVFS(`${fontName}-bold.ttf`, arrayBufferToBase64(boldFontBytes));
    doc.addFont(`${fontName}-bold.ttf`, fontName, 'bold');
    return true;
  } catch (error) {
    console.warn('Failed to load Amiri font. Using fallback.');
    return false;
  }
};

// NEW: Wrap text only by whole words, never split a word
const wrapTextNoSplit = (text: string, maxWidth: number, doc: jsPDF, font: string, fontSize: number): string[] => {
  doc.setFont(font, 'normal');
  doc.setFontSize(fontSize);
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const width = doc.getTextWidth(testLine);
    if (width > maxWidth) {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        // Even a single word is too wide, must use it as-is (will overflow or shrink font later)
        lines.push(word);
        currentLine = '';
      }
    } else {
      currentLine = testLine;
    }
    if (lines.length >= 2) break;
  }
  if (currentLine) lines.push(currentLine);
  return lines;
};

const exportToPDF = async (
  data: any[][],
  periodLabel: string,
  departmentsLabel: string,
  headers: string[],
  isRtl: boolean,
  fileNamePrefix: string
) => {
  const toastId = 'pdf-export';
  try {
    toast.info(isRtl ? 'جارٍ إنشاء ملف PDF...' : 'Generating PDF...', {
      position: isRtl ? 'top-left' : 'top-right',
      autoClose: false,
      toastId,
    });

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const fontLoaded = await loadAmiriFont(doc);
    const fontName = fontLoaded ? 'Amiri' : 'helvetica';
    const pageWidth = doc.internal.pageSize.width;

    // === Header ===
    doc.setFont(fontName, 'bold');
    doc.setFontSize(18);
    doc.setTextColor(40, 40, 40);
    const title = isRtl ? 'ملخص الطلبات اليومية' : 'Daily Orders Summary';
    doc.text(title, pageWidth / 2, 20, { align: 'center' });

    doc.setFontSize(11);
    doc.setTextColor(80, 80, 80);
    doc.text(periodLabel, pageWidth / 2, 28, { align: 'center' });

    if (departmentsLabel) {
      doc.setFontSize(10);
      const deptLines = wrapTextNoSplit(departmentsLabel, pageWidth - 40, doc, fontName, 10);
      doc.text(deptLines, pageWidth / 2, 35, { align: 'center' });
    }

    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.line(15, 43, pageWidth - 15, 43);

    // === Table ===
    const processedData = isRtl ? data.map(row => row.slice().reverse()) : data;
    const processedHeaders = isRtl ? headers.slice().reverse() : headers;
    const totalColumns = processedHeaders.length;
    const contentWidth = pageWidth - 30; // Margins: 15mm left + 15mm right

    // Distribute width more intelligently
    const firstColMin = 40;   // Minimum for product name
    const lastColMin = 25;    // Minimum for total
    const branchMin = 20;     // Minimum per branch (must be as small as readable)

    const availableWidth = contentWidth - firstColMin - lastColMin;
    const branchColWidth = Math.max(branchMin, availableWidth / Math.max(1, totalColumns - 2));
    const firstColWidth = Math.min(70, firstColMin + Math.max(0, (availableWidth - (totalColumns - 2) * branchColWidth) / 2));
    const lastColWidth = lastColMin;

    // Ensure total doesn't exceed contentWidth
    const totalTableWidth = firstColWidth + (totalColumns - 2) * branchColWidth + lastColWidth;
    if (totalTableWidth > contentWidth) {
      // If still too wide, compress proportionally
      const scaleFactor = contentWidth / totalTableWidth;
      const scaledFirst = firstColWidth * scaleFactor;
      const scaledBranch = branchColWidth * scaleFactor;
      const scaledLast = lastColWidth * scaleFactor;

      autoTable(doc, {
        head: [processedHeaders],
        body: processedData,
        startY: 49,
        theme: 'grid',
        tableWidth: contentWidth,
        headStyles: {
          fillColor: [250, 240, 220],
          textColor: [70, 60, 40],
          fontSize: 8,
          halign: 'center',
          valign: 'middle',
          overflow: 'linebreak',
          font: fontName,
          fontStyle: 'bold',
          cellPadding: 2,
        },
        bodyStyles: {
          fontSize: 8,
          halign: 'center',
          valign: 'middle',
          font: fontName,
          textColor: [50, 50, 50],
          cellPadding: 2,
          minCellHeight: 10,
        },
        alternateRowStyles: { fillColor: [254, 253, 250] },
        columnStyles: {
          0: { cellWidth: scaledFirst, minCellHeight: 10, overflow: 'linebreak' },
          ...Object.fromEntries(
            Array.from({ length: totalColumns - 2 }, (_, i) => [
              i + 1,
              { cellWidth: scaledBranch, minCellHeight: 10, overflow: 'linebreak' },
            ])
          ),
          [totalColumns - 1]: { cellWidth: scaledLast, minCellHeight: 10 },
        },
        willDrawCell: (hookData) => {
          if ((hookData.section === 'body' && hookData.column.index === 0) || hookData.section === 'head') {
            const text = hookData.cell.text[0] || '';
            hookData.cell.text = wrapTextNoSplit(text, hookData.cell.width - 4, doc, fontName, 8);
          }
        },
        didParseCell: (hookData) => {
          hookData.cell.text = hookData.cell.text.map((text: string) =>
            /[0-9]/.test(text) ? toArabicNumerals(text) : text
          );
          if (hookData.section === 'body' && hookData.row.index === hookData.table.body.length - 1) {
            hookData.cell.styles.fontStyle = 'bold';
            hookData.cell.styles.fillColor = [245, 235, 215];
          }
          if (hookData.section === 'body' && hookData.column.index > 0 && hookData.column.index < totalColumns - 1) {
            const value = Number(hookData.cell.raw);
            if (value > 0) {
              hookData.cell.styles.fillColor = [220, 255, 220];
            }
          }
        },
      });
    } else {
      // Normal case: fits within contentWidth
      autoTable(doc, {
        head: [processedHeaders],
        body: processedData,
        startY: 49,
        theme: 'grid',
        tableWidth: totalTableWidth,
        headStyles: {
          fillColor: [250, 240, 220],
          textColor: [70, 60, 40],
          fontSize: 8,
          halign: 'center',
          valign: 'middle',
          overflow: 'linebreak',
          font: fontName,
          fontStyle: 'bold',
          cellPadding: 2,
        },
        bodyStyles: {
          fontSize: 8,
          halign: 'center',
          valign: 'middle',
          font: fontName,
          textColor: [50, 50, 50],
          cellPadding: 2,
          minCellHeight: 10,
        },
        alternateRowStyles: { fillColor: [254, 253, 250] },
        columnStyles: {
          0: { cellWidth: firstColWidth, minCellHeight: 10, overflow: 'linebreak' },
          ...Object.fromEntries(
            Array.from({ length: totalColumns - 2 }, (_, i) => [
              i + 1,
              { cellWidth: branchColWidth, minCellHeight: 10, overflow: 'linebreak' },
            ])
          ),
          [totalColumns - 1]: { cellWidth: lastColWidth, minCellHeight: 10 },
        },
        willDrawCell: (hookData) => {
          if ((hookData.section === 'body' && hookData.column.index === 0) || hookData.section === 'head') {
            const text = hookData.cell.text[0] || '';
            hookData.cell.text = wrapTextNoSplit(text, hookData.cell.width - 4, doc, fontName, 8);
          }
        },
        didParseCell: (hookData) => {
          hookData.cell.text = hookData.cell.text.map((text: string) =>
            /[0-9]/.test(text) ? toArabicNumerals(text) : text
          );
          if (hookData.section === 'body' && hookData.row.index === hookData.table.body.length - 1) {
            hookData.cell.styles.fontStyle = 'bold';
            hookData.cell.styles.fillColor = [245, 235, 215];
          }
          if (hookData.section === 'body' && hookData.column.index > 0 && hookData.column.index < totalColumns - 1) {
            const value = Number(hookData.cell.raw);
            if (value > 0) {
              hookData.cell.styles.fillColor = [220, 255, 220];
            }
          }
        },
      });
    }

    // === Footer ===
    const dateStr = new Date().toLocaleDateString(isRtl ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    const footer = isRtl
      ? `نظام إدارة الجودياء – ${toArabicNumerals(dateStr)}`
      : `Elgoodia Management System – ${dateStr}`;
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(footer, pageWidth / 2, doc.internal.pageSize.height - 10, { align: 'center' });

    const fileName = `${fileNamePrefix}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    toast.update(toastId, {
      render: isRtl ? 'تم التصدير بنجاح!' : 'Exported successfully!',
      type: 'success',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('PDF Export Error:', error);
    toast.update(toastId, {
      render: isRtl ? 'فشل التصدير. حاول مرة أخرى.' : 'Export failed. Please try again.',
      type: 'error',
      autoClose: 4000,
    });
  }
};

// ==============================
// === MAIN COMPONENT ===
// ==============================
const DailyOrdersSummary: React.FC = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isRtl = language === 'ar';

  const [ordersData, setOrdersData] = useState<OrderRow[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>(['all']);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [period, setPeriod] = useState('today');

  const periodOptions = useMemo(
    () => PERIOD_OPTIONS.map(p => ({
      value: p.value,
      label: isRtl ? p.labelAr : p.labelEn
    })),
    [isRtl]
  );

  const fetchDepartments = useCallback(async () => {
    try {
      const response = await departmentAPI.getAll({ limit: 100 });
      const data = Array.isArray(response) ? response : (response.data || []);
      setDepartments(data);
    } catch (err) {
      toast.error(isRtl ? 'فشل جلب الأقسام' : 'Failed to load departments');
    }
  }, [isRtl]);

  const fetchData = useCallback(async () => {
    if (!user || (user.role !== 'admin' && user.role !== 'production')) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [departmentsRes, branchesRes] = await Promise.all([
        departmentAPI.getAll({ limit: 100 }),
        branchesAPI.getAll()
      ]);

      const departmentsData = Array.isArray(departmentsRes) ? departmentsRes : (departmentsRes.data || []);
      setDepartments(departmentsData);

      const branchesData = Array.isArray(branchesRes)
        ? branchesRes.map(b => ({
            _id: b._id,
            name: b.name || (isRtl ? 'غير معروف' : 'Unknown'),
            nameEn: b.nameEn,
            displayName: isRtl ? b.name : (b.nameEn || b.name),
          }))
        : [];
      setBranches(branchesData);

      const productsRes = await productsAPI.getAll({ limit: 0 });
      const ordersRes = await ordersAPI.getAll({ status: { $ne: 'cancelled' } });

      const productsData = Array.isArray(productsRes)
        ? productsRes.map(p => ({
            _id: p._id,
            name: p.name,
            nameEn: p.nameEn,
            unit: p.unit || 'قطع',
            unitEn: p.unitEn || p.unit || 'Pieces',
            department: p.department || { _id: 'general', name: isRtl ? 'عام' : 'General' },
          }))
        : [];

      const productMap = new Map(productsData.map(p => [p._id, p]));
      const branchMap = new Map(branchesData.map(b => [b._id, b.displayName]));
      const allBranchNames = branchesData.map(b => b.displayName);

      const orderMap = new Map<string, OrderRow>();
      const orders = Array.isArray(ordersRes) ? ordersRes : (ordersRes.orders || []);

      const now = new Date();
      let start: Date, end: Date;
      if (period === 'custom') {
        start = startDate ? new Date(startDate) : startOfDay(now);
        end = endDate ? new Date(endDate) : endOfDay(now);
        end.setHours(23, 59, 59, 999);
      } else {
        switch (period) {
          case 'today':
            // TODAY: From 12:00 PM (noon) TODAY to 11:59:59 PM (midnight) TOMORROW
            start = addHours(startOfDay(now), 12); // 12:00 PM today
            end = endOfDay(now); // 11:59:59 PM today
            break;
          case 'yesterday':
            // YESTERDAY: From 12:00 PM YESTERDAY to 11:59:59 PM YESTERDAY
            const yesterday = subDays(now, 1);
            start = addHours(startOfDay(yesterday), 12); // 12:00 PM yesterday
            end = endOfDay(yesterday); // 11:59:59 PM yesterday
            break;
          case 'week':
            start = startOfDay(subDays(now, 6));
            end = endOfDay(now);
            break;
          case 'month':
            start = startOfDay(subMonths(now, 1));
            end = endOfDay(now);
            break;
          default:
            start = addHours(startOfDay(now), 12);
            end = endOfDay(now);
        }
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(end.toISOString().split('T')[0]);
      }

      const validOrders = orders.filter(order =>
        order.createdAt &&
        isValidDate(order.createdAt) &&
        new Date(order.createdAt) >= start &&
        new Date(order.createdAt) <= end
      );

      validOrders.forEach(order => {
        (order.items || []).forEach((item: any) => {
          if (!item.product?._id) return;
          const productId = item.product._id;
          const deptId = item.product.department?._id || 'general';

          const product = productMap.get(productId) || {
            name: item.product.name || (isRtl ? 'منتج محذوف' : 'Deleted'),
            nameEn: item.product.nameEn,
            unit: item.product.unit || 'قطع',
            unitEn: item.product.unitEn || 'Pieces',
            department: { _id: deptId, name: isRtl ? 'عام' : 'General' },
          };

          const displayName = isRtl ? product.name : (product.nameEn || product.name);
          const unit = isRtl ? (product.unit || 'قطعة') : (product.unitEn || 'Piece');

          if (!orderMap.has(productId)) {
            const initialBranchQuantities: { [key: string]: number } = {};
            allBranchNames.forEach(branchName => {
              initialBranchQuantities[branchName] = 0;
            });

            orderMap.set(productId, {
              id: productId,
              product: displayName,
              unit,
              department: isRtl ? product.department.name : (product.department.nameEn || product.department.name),
              departmentId: deptId,
              branchQuantities: initialBranchQuantities,
              totalQuantity: 0,
            });
          }

          const row = orderMap.get(productId)!;
          const branchName = branchMap.get(order.branch?._id) || (isRtl ? 'الفرع الرئيسي' : 'Main Branch');
          if (!(branchName in row.branchQuantities)) {
            row.branchQuantities[branchName] = 0;
          }
          const qty = Number(item.quantity) || 0;
          row.branchQuantities[branchName] += qty;
          row.totalQuantity += qty;
        });
      });

      setOrdersData(Array.from(orderMap.values()).sort((a, b) => b.totalQuantity - a.totalQuantity));
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error(isRtl ? 'فشل جلب البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [user, period, startDate, endDate, isRtl]);

  useEffect(() => {
    setPeriod('today');
  }, []);

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'production')) {
      fetchData();
    }
  }, [period, startDate, endDate, user, isRtl, fetchData]);

  const departmentOptions = useMemo(() => {
    const baseOptions = departments.map(d => ({
      value: d._id,
      label: isRtl ? d.name : (d.nameEn || d.name),
    })).sort((a, b) => a.label.localeCompare(b.label));
    // Add 'All' at the END
    return [...baseOptions, { value: 'all', label: isRtl ? 'جميع الأقسام' : 'All Departments' }];
  }, [departments, isRtl]);

  const filteredData = useMemo(() => {
    const allSelected = selectedDepartments.includes('all');
    return ordersData.filter(row => {
      const matchesSearch = row.product.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDepartment = allSelected || selectedDepartments.includes(row.departmentId);
      return matchesSearch && matchesDepartment;
    });
  }, [ordersData, searchTerm, selectedDepartments]);

  const allBranches = useMemo(() => branches.map(b => b.displayName).sort(), [branches]);
  const grandTotalQuantity = useMemo(() => filteredData.reduce((sum, row) => sum + row.totalQuantity, 0), [filteredData]);

  const periodLabel = useMemo(() => {
    if (period === 'custom') {
      if (isSameDay(parseISO(startDate), parseISO(endDate))) {
        return isRtl 
          ? `يوم واحد - ${format(parseISO(startDate), 'EEEE d/M/yyyy', { locale: arSA })}`
          : `Single Day - ${format(parseISO(startDate), 'EEEE d/M/yyyy')}`;
      }
      return `${format(parseISO(startDate), 'd/M/yyyy')} - ${format(parseISO(endDate), 'd/M/yyyy')}`;
    }

    // Handle "yesterday" with full date and day name
    if (period === 'yesterday') {
      const yesterday = subDays(new Date(), 1);
      return isRtl
        ? `أمس - ${format(yesterday, 'EEEE d/M/yyyy', { locale: arSA })}`
        : `Yesterday - ${format(yesterday, 'EEEE d/M/yyyy')}`;
    }

    return periodOptions.find(p => p.value === period)?.label || '';
  }, [period, startDate, endDate, isRtl, periodOptions]);

  const departmentsLabel = useMemo(() => {
    if (selectedDepartments.includes('all')) return '';
    const selectedCount = selectedDepartments.filter(v => v !== 'all').length;
    const selectedNames = selectedDepartments
      .filter(v => v !== 'all')
      .map(id => departments.find(d => d._id === id)?.name || '')
      .filter(Boolean);

    // Always show full list with " - " separator, no limit
    return isRtl 
      ? `الأقسام: ${selectedNames.join(' - ')}` 
      : `Departments: ${selectedNames.join(' - ')}`;
  }, [selectedDepartments, departments, isRtl]);

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    setPeriod('custom');
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
    setPeriod('custom');
  };

  if (!user || (user.role !== 'admin' && user.role !== 'production')) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <span className="text-xs text-red-600">{isRtl ? 'غير مصرح لك' : 'Not authorized'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 mx-auto">
      {/* Full-Width Header */}
      <div className="w-full mb-4">
        <div className="flex justify-between items-center align-center gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-100 rounded-xl">
              <Calendar className="w-5 h-5 text-amber-700" />
            </div>
            <h1 className="text-xl font-bold text-gray-800">
              {isRtl ? 'ملخص الطلبات اليومية' : 'Daily Orders Summary'}
            </h1>
          </div>

          <div className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <Button
              variant="primary"
              onClick={() => {
                const headers = [isRtl ? 'اسم المنتج' : 'Product Name', ...allBranches, isRtl ? 'الإجمالي' : 'Total'];
                const rows = [
                  ...filteredData.map(row => [
                    row.product,
                    ...allBranches.map(b => formatNumber(row.branchQuantities[b] || 0, isRtl)),
                    `${formatNumber(row.totalQuantity, isRtl)} ${row.unit}`,
                  ]),
                  [
                    isRtl ? 'الإجمالي' : 'Total',
                    ...allBranches.map(b => formatNumber(filteredData.reduce((sum, row) => sum + (row.branchQuantities[b] || 0), 0), isRtl)),
                    formatNumber(grandTotalQuantity, isRtl),
                  ],
                ];
                exportToPDF(rows, periodLabel, departmentsLabel, headers, isRtl, 'DailyOrdersSummary');
              }}
              disabled={filteredData.length === 0}
              icon={<Download className="w-4 h-4" />}
            >
              {isRtl ? 'PDF' : 'PDF'}
            </Button>
          </div>
        </div>

        {/* Filters Layout */}
        <div className="flex flex-col lg:flex-row gap-3 items-center w-full">
          <div className="w-full lg:w-1/2">
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              {isRtl ? 'بحث المنتج' : 'Search Product'}
            </label>
            <ProductSearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={isRtl ? 'ابحث عن منتج...' : 'Search product...'}
              ariaLabel={isRtl ? 'بحث المنتج' : 'Product search'}
            />
          </div>

          <div className="flex flex-col md:flex-row gap-3 w-full lg:w-1/2">
            <div className="w-full md:w-1/2">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {isRtl ? 'الأقسام' : 'Departments'}
              </label>
              <MultiSelectDropdown
                value={selectedDepartments}
                onChange={setSelectedDepartments}
                options={departmentOptions}
                ariaLabel={isRtl ? 'اختر الأقسام' : 'Select departments'}
              />
            </div>

            <div className="w-full md:w-1/2">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {isRtl ? 'الفترة' : 'Period'}
              </label>
              <PeriodDropdown
                value={period}
                onChange={setPeriod}
                options={periodOptions}
                ariaLabel={isRtl ? 'اختر الفترة' : 'Select period'}
              />
            </div>
          </div>
        </div>

        {period === 'custom' && (
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {isRtl ? 'من تاريخ' : 'From Date'}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                className="w-full py-2 px-3 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                dir="ltr"
              />
            </div>
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {isRtl ? 'إلى تاريخ' : 'To Date'}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                className="w-full py-2 px-3 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                dir="ltr"
              />
            </div>
          </div>
        )}
      </div>

      {/* Data Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-600"></div>
        </div>
      ) : filteredData.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="inline-block p-3 bg-gray-100 rounded-full mb-3">
            <Filter className="w-6 h-6 text-gray-500" />
          </div>
          <p className="text-gray-600 text-sm">{isRtl ? 'لا توجد بيانات مطابقة' : 'No matching data found'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full w-full text-xs border-collapse">
              <thead>
                <tr className="bg-amber-50">
                  <th className="px-3 py-2.5 font-semibold text-gray-700 text-center whitespace-nowrap">{isRtl ? 'اسم المنتج' : 'Product Name'}</th>
                  {allBranches.map(branch => (
                    <th key={branch} className="px-2 py-2.5 font-semibold text-gray-700 text-center whitespace-nowrap min-w-[60px]">{branch}</th>
                  ))}
                  <th className="px-3 py-2.5 font-semibold text-gray-700 text-center whitespace-nowrap">{isRtl ? 'الإجمالي' : 'Total'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredData.map((row, idx) => (
                  <tr
                    key={row.id}
                    className={`hover:bg-amber-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                  >
                    <td className="px-3 py-2.5 text-gray-800 text-center max-w-[120px] truncate" title={row.product}>
                      {row.product}
                    </td>
                    {allBranches.map(branch => {
                      const quantity = row.branchQuantities[branch] || 0;
                      const cellClass = `px-2 py-2.5 text-gray-700 text-center ${quantity > 0 ? 'bg-green-100 font-medium' : ''}`;
                      return (
                        <td key={branch} className={cellClass}>
                          {formatNumber(quantity, isRtl)}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2.5 text-gray-800 text-center font-medium">
                      {formatNumber(row.totalQuantity, isRtl)} <span className="text-gray-500 text-[10px] ml-1">{row.unit}</span>
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="bg-amber-100 font-bold">
                  <td className="px-3 py-2.5 text-gray-800 text-center">{isRtl ? 'الإجمالي' : 'Total'}</td>
                  {allBranches.map(branch => (
                    <td key={branch} className="px-2 py-2.5 text-gray-800 text-center">
                      {formatNumber(filteredData.reduce((sum, row) => sum + (row.branchQuantities[branch] || 0), 0), isRtl)}
                    </td>
                  ))}
                  <td className="px-3 py-2.5 text-gray-800 text-center">
                    {formatNumber(grandTotalQuantity, isRtl)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyOrdersSummary;